import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';
import { defaultGcTime, defaultStaleTime } from '@/shared/constants';
import { useQuery } from '@tanstack/react-query';

import { get } from '@truefit/http-utils';

import { type OutputProfileMode } from './useOutputProfilesQuery';

/**
 * The engine `output` block of a profile. Only `res` is guaranteed; every other
 * encoder knob (fps, tune, bitrate, ...) is optional and passed through as-is.
 */
export interface TranscodingProfileOutput {
  res: { width: number; height: number };
  fps?: number[];
  tune?: string;
  avg_bitrate?: number;
  max_bitrate?: number;
  // Advanced encoder knobs (see the player's `output` config).
  codec?: string;
  preset?: number;
  gop_length?: number;
  n_ps?: number;
  [key: string]: unknown;
}

/** A profile as returned by the editor endpoint, including its current usage. */
export interface TranscodingProfile {
  id: number;
  /** Stable slug / machine id (unique), e.g. "tp_broadcast_1080". */
  name: string;
  /** Human title shown in the editor, e.g. "Broadcast 1080p59.94". */
  displayName: string | null;
  description: string | null;
  output: TranscodingProfileOutput;
  /** Modes pointing at this profile; non-empty ⇒ deletion is blocked. */
  assignedModes: OutputProfileMode[];
}

export const TRANSCODING_PROFILES_QUERY_KEY = ['TranscodingProfiles'];

const TRANSCODING_PROFILES_URL = '/v1.0/internals/transcoding-profiles';

export const useTranscodingProfilesQuery = () => {
  const { sessionState } = useAuth();
  const isReady = isSignedIn(sessionState);

  return useQuery({
    queryKey: TRANSCODING_PROFILES_QUERY_KEY,
    queryFn: async () => {
      const { data } = await get<TranscodingProfile[]>(TRANSCODING_PROFILES_URL);
      return data;
    },
    enabled: isReady,
    staleTime: defaultStaleTime,
    gcTime: defaultGcTime,
  });
};

export { TRANSCODING_PROFILES_URL };
export default useTranscodingProfilesQuery;
